import { namespaceCopyFor } from '@/i18n/ns';
import companyErrors from '@/i18n/ns/company-errors';
import workspaceErrors from '@/i18n/ns/workspace-errors';
import collaborationErrors from '@/i18n/ns/workspace-collaboration';
import attachmentErrors from '@/i18n/ns/attachment-errors';

const localeKey = (locale?: string) => (locale ?? 'en').toLowerCase().split(/[-_]/)[0];
const templates = [
  workspaceErrors.en,
  companyErrors.en,
  collaborationErrors.en,
  attachmentErrors.en,
] as const;
const patterns = templates.flatMap((catalog) =>
  Object.entries(catalog).map(([key, english]) => {
    const names: string[] = [];
    const parts = english.split(/(\{[A-Za-z][A-Za-z0-9_]*\})/g);
    const pattern = parts
      .map((part) => {
        if (part.startsWith('{') && part.endsWith('}')) {
          names.push(part.slice(1, -1));
          return '([\\s\\S]*?)';
        }
        return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      })
      .join('');
    return { key, names, pattern: new RegExp(`^${pattern}$`), catalog };
  }),
);

/** Exact authored error templates only; filenames and unknown diagnostics remain data. */
export function localizeWorkspaceError(message: string, locale?: string): string {
  const language = localeKey(locale);
  const workspace = namespaceCopyFor(workspaceErrors, language);
  const company = namespaceCopyFor(companyErrors, language);
  for (const { key, names, pattern, catalog } of patterns) {
    const match = pattern.exec(message);
    if (!match) continue;
    const values = new Map(names.map((name, index) => [name, match[index + 1]]));
    const copy = (
      catalog === workspaceErrors.en
        ? workspace
        : catalog === companyErrors.en
          ? company
          : catalog === collaborationErrors.en
            ? namespaceCopyFor(collaborationErrors, language)
            : namespaceCopyFor(attachmentErrors, language)
    ) as Record<string, string>;
    return copy[key].replace(
      /\{([^}]+)\}/g,
      (placeholder: string, name: string) => values.get(name) ?? placeholder,
    );
  }
  return message;
}
